import {
  observeConversation,
  postUserMessage,
} from "@mirage/service-conversation";
import { convertChatSourceToDocumentInfo } from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import { createConversationClientContext } from "@mirage/service-conversation/utils/createConversationClientContext";
import { LoadingStatus } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/doc_summarization";
import {
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import { tagged } from "@mirage/service-logging";
import i18n from "@mirage/translations";
import { useCallback, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import type { SearchResult } from "@mirage/service-dbx-api";

const logger = tagged("document-summary-service");

export type SummaryState = {
  summary: string | null;
  status: LoadingStatus;
  isLoading: boolean;
  error: Error | null;
  conversationId: string | null;
};

// Global cache to store summaries by document UUID
const summaryCache = new Map<string, SummaryState>();

// Export cache for testing
export { summaryCache };

export function useDocumentSummary(result: SearchResult) {
  // Check if we have a cached summary for this document
  const cachedSummary = summaryCache.get(result.uuid);

  const [state, setState] = useState<SummaryState>(
    cachedSummary || {
      summary: null,
      status: LoadingStatus.pending,
      isLoading: false,
      error: null,
      conversationId: null,
    },
  );

  // Update cache whenever state changes
  const updateState = useCallback(
    (newState: SummaryState | ((prev: SummaryState) => SummaryState)) => {
      setState((prev) => {
        const updatedState =
          typeof newState === "function" ? newState(prev) : newState;
        // Cache the summary if it's successful
        if (
          updatedState.summary &&
          updatedState.status === LoadingStatus.success
        ) {
          summaryCache.set(result.uuid, updatedState);
        }
        return updatedState;
      });
    },
    [result.uuid],
  );

  // create a stable conversation ID based on the document ID
  // this ensures we use the same conversation ID for the same document
  const conversationId = useMemo(() => {
    // if the document has a UUID, use it to create a deterministic conversation ID
    // helps with potential caching and avoids creating new conversations for the same document
    const baseId = result.uuid || uuid();
    return `summary-${baseId}`;
  }, [result.uuid]);

  // Create a stable reference to the result that only changes when essential properties change
  const stableResult = useMemo(
    () => result,
    [result.uuid, result.url, result.title],
  );

  // Track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);

  const getSummary = useCallback(async () => {
    // If we already have a successful summary, don't fetch again
    if (state.summary && state.status === LoadingStatus.success) {
      return;
    }

    // Prevent duplicate requests
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    try {
      updateState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      // Note: This creates a temporary conversation that won't appear in the user's conversation history
      // We use skipPersistence: true to ensure this is a temporary conversation just for getting the summary
      await postUserMessage({
        agentIds: ["summarize"],
        message: i18n.t("summarize_this_document"),
        attachments: [
          convertChatSourceToDocumentInfo({
            type: "mirage_search_result",
            searchResult: stableResult,
          }),
        ],
        source: "document_preview",
        conversationId,
        clientContext: createConversationClientContext("serp-answer", {
          clientScenarioId: conversationId,
        }),
        skipPersistence: true,
        searchParams: new SearchParams({
          searchTarget: {
            value: new NoSearch(),
            case: "noSearch",
          },
        }),
      });

      const subscription = observeConversation(conversationId).subscribe({
        next: (conversation) => {
          const assistantMessage = conversation.messages
            .filter((msg) => msg.messageType.case === "assistantMessage")
            .pop();

          if (assistantMessage?.messageType.case === "assistantMessage") {
            const text = assistantMessage.messageType.value?.text;
            if (text) {
              const isStreaming = assistantMessage.status === "streaming";
              if (!isStreaming) {
                isFetchingRef.current = false;
              }
              updateState((prev) => ({
                ...prev,
                summary: text,
                status: isStreaming
                  ? LoadingStatus.pending
                  : LoadingStatus.success,
                isLoading: isStreaming,
                conversationId,
              }));
            }
          }
        },
        error: (error) => {
          logger.warn("Failed to get document summary", { error });
          isFetchingRef.current = false;
          updateState((prev) => ({
            ...prev,
            error,
            isLoading: false,
            status: LoadingStatus.error,
            conversationId: null,
          }));
        },
      });

      return () => subscription.unsubscribe();
    } catch (error) {
      logger.warn("Failed to get document summary", { error });
      isFetchingRef.current = false;
      updateState((prev) => ({
        ...prev,
        error: error as Error,
        isLoading: false,
        status: LoadingStatus.error,
        conversationId: null,
      }));
    }
  }, [stableResult, conversationId, updateState, state.summary, state.status]);

  return {
    summary: state.summary,
    isLoading: state.isLoading,
    error: state.error,
    conversationId: state.conversationId,
    getSummary,
  };
}
